/** Edit the provider-reviewed exact-match filters without exposing raw queries. */
import { useState } from 'react'
import { ChevronDown, Plus, X } from 'lucide-react'

import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui'
import { cn } from '@/lib/utils'
import type {
  KualiResearchFilter,
  KualiResearchFilterField,
} from '../types'

const MAX_FILTERS = 5
const filterFieldLabels: Record<KualiResearchFilter['field'], string> = {
  statusCode: 'Award status',
  awardTypeCode: 'Award type',
  activityTypeCode: 'Activity type',
  unitNumber: 'Lead unit code',
  sponsorCode: 'Sponsor code',
  isFederal: 'Is federal',
  primeFederal: 'Prime federal',
}

export function KualiResearchFilters({
  fields,
  filters,
  loading = false,
  error = false,
  onRetry,
  onChange,
}: {
  fields: KualiResearchFilterField[]
  filters: KualiResearchFilter[]
  loading?: boolean
  error?: boolean
  onRetry?: () => void
  onChange: (filters: KualiResearchFilter[]) => void
}) {
  const [addOpen, setAddOpen] = useState(false)
  const unused = fields.filter(field => !filters.some(filter => filter.field === field.field))
  const canAdd = filters.length < MAX_FILTERS && unused.length > 0
  const update = (index: number, value: KualiResearchFilter) => {
    onChange(filters.map((filter, position) => position === index ? value : filter))
  }

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {filters.map((filter, index) => (
          <FilterChip
            key={filter.field}
            index={index}
            filter={filter}
            field={fields.find(value => value.field === filter.field)}
            availableFields={fields.filter(candidate => (
              candidate.field === filter.field || unused.includes(candidate)
            ))}
            onChange={value => update(index, value)}
            onRemove={() => onChange(filters.filter((_, position) => position !== index))}
          />
        ))}
        <Popover open={addOpen} onOpenChange={setAddOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-[29px] rounded-[8px] border-dashed px-2.5 text-[12px] text-slate-500"
              disabled={!canAdd || loading || error}
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add filter
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[230px] rounded-[10px] p-1.5">
            <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Choose a filter
            </p>
            {unused.slice(0, MAX_FILTERS - filters.length).map(field => (
              <Button
                key={field.field}
                type="button"
                variant="ghost"
                className="h-8 w-full justify-start rounded-[7px] px-2 text-xs font-medium text-slate-700"
                onClick={() => {
                  onChange([...filters, newKualiResearchFilter(field)])
                  setAddOpen(false)
                }}
              >
                {field.label}
              </Button>
            ))}
          </PopoverContent>
        </Popover>
      </div>

      {loading && (
        <p role="status" className="text-[11.5px] text-slate-400">Loading filter choices…</p>
      )}
      {error && (
        <div role="alert" className="flex items-center gap-2 text-[11.5px] text-amber-700">
          <span>Filter choices could not be loaded. Existing filters are preserved.</span>
          {onRetry && (
            <Button type="button" variant="link" className="h-auto p-0 text-[11.5px]" onClick={onRetry}>
              Retry
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function FilterChip({
  index,
  filter,
  field,
  availableFields,
  onChange,
  onRemove,
}: {
  index: number
  filter: KualiResearchFilter
  field?: KualiResearchFilterField
  availableFields: KualiResearchFilterField[]
  onChange: (filter: KualiResearchFilter) => void
  onRemove: () => void
}) {
  const validChoice = !field?.choices.length
    || field.choices.some(choice => choice.value === filter.value)
  const valueLabel = field?.choices.find(choice => choice.value === filter.value)?.label
    ?? (validChoice ? filter.value : 'Choose value')
  const label = field?.label ?? filterFieldLabels[filter.field]
  const content = <>{label}: {valueLabel || 'Choose value'}</>

  return (
    <div className="flex h-[29px] overflow-hidden rounded-[8px] border border-slate-200 bg-slate-100">
      {field ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className={cn(
                'h-full rounded-none px-2.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-200',
                !validChoice && 'text-amber-700',
              )}
            >
              {content}<ChevronDown className="ml-1.5 h-3 w-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[300px] space-y-2.5 rounded-[10px] p-3">
            <Select
              value={filter.field}
              onValueChange={value => {
                const next = availableFields.find(candidate => candidate.field === value)
                if (next) onChange(newKualiResearchFilter(next))
              }}
            >
              <SelectTrigger aria-label={`Filter ${index + 1} field`} className="h-8 text-xs">
                {label}
              </SelectTrigger>
              <SelectContent>
                {availableFields.map(candidate => (
                  <SelectItem key={candidate.field} value={candidate.field}>{candidate.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FilterValue
              ariaLabel={`Filter ${index + 1} value`}
              field={field}
              value={filter.value}
              onChange={value => onChange({ ...filter, value })}
            />
          </PopoverContent>
        </Popover>
      ) : (
        <span className="inline-flex items-center px-2.5 text-[12px] font-semibold text-amber-700">
          {content}
        </span>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Remove ${label} filter`}
        className="h-full w-7 rounded-none border-l border-slate-200 text-slate-400 hover:bg-slate-200 hover:text-destructive"
        onClick={onRemove}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  )
}

function FilterValue({
  ariaLabel,
  field,
  value,
  onChange,
}: {
  ariaLabel: string
  field: KualiResearchFilterField
  value: string
  onChange: (value: string) => void
}) {
  if (field.choices.length) {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-label={ariaLabel} className="h-8 text-xs">
          {field.choices.find(choice => choice.value === value)?.label ?? 'Choose value'}
        </SelectTrigger>
        <SelectContent>
          {field.choices.map(choice => (
            <SelectItem key={choice.value} value={choice.value}>{choice.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }
  return (
    <Input
      aria-label={ariaLabel}
      value={value}
      maxLength={40}
      placeholder={field.field.toLowerCase().includes('unit') ? 'e.g. CC011202' : 'Enter exact code'}
      className="h-8 font-mono text-xs"
      onChange={event => onChange(event.target.value.toUpperCase())}
    />
  )
}

export function newKualiResearchFilter(
  field: KualiResearchFilterField,
): KualiResearchFilter {
  return { field: field.field, value: field.choices[0]?.value ?? '' }
}

export function setKualiResearchFilterValue(
  fields: KualiResearchFilterField[],
  filters: KualiResearchFilter[],
  fieldName: KualiResearchFilter['field'],
  value: string,
): KualiResearchFilter[] {
  if (!fields.some(field => field.field === fieldName)) return filters
  const existing = filters.findIndex(filter => filter.field === fieldName)
  const selected = { field: fieldName, value }
  if (existing < 0) return filters.length < MAX_FILTERS ? [...filters, selected] : filters
  return filters.map((filter, index) => index === existing ? selected : filter)
}
